import { isErrorValidationErrorResponse } from "utilities/validationErrorHelpers";
import { ResponseError, ResponseErrorforRC } from "./responseError";

export async function throwIfResponseError(response: Response) {
  // Check for maintenance mode FIRST
  if (response.status === 503) {
    await handleMaintenanceMode(response);
    return;
  }
  
  if (response.status === 500) {
    await throwHttp500Error(response, "The server encountered a fatal error.");
  } else if (response.status === 400) {
    await throwResponseError(response, "The request sent to the server was invalid.");
  } else if (response.status === 401) {
    await throwResponseError(response, "The requested resource requires authentication to access.");
  } else if (response.status === 403) {
    await throwResponseError(response, "You do not have permission to access the requested resource.");
  } else if (response.status === 404) {
    await throwResponseError(response, "The requested resource was not found.");
  } else if (response.status === 504) {
    await throwResponseError(response, "The requested resource took longer than expected. Please try again later.");
  } else if (response.status < 200 || response.status > 299) {
    await throwResponseError(response, `The server returned an error (${response.statusText}).`);
  } 
}

async function handleMaintenanceMode(response: Response) {
  let maintenanceMessage = "WorkRight Authoring is temporarily unavailable due to maintenance. Please try again later.";
  let isMaintenanceModeEnabled = false;
  
  try {
    // Try to get custom message from response
    const jsonResponse = await response.json();
    
    // Check if maintenance mode is explicitly enabled
    if (jsonResponse.maintenanceMode === true) {
      isMaintenanceModeEnabled = true;
      
      if (jsonResponse.message) {
        maintenanceMessage = jsonResponse.message;
      }
    }
  } catch {
    // If JSON parsing fails, check headers
    const maintenanceModeHeader = response.headers.get('X-Maintenance-Mode');
    
    // Only enable maintenance mode if header explicitly says "true"
    if (maintenanceModeHeader === 'true') {
      isMaintenanceModeEnabled = true;
      
      const headerMessage = response.headers.get('X-Maintenance-Message');
      if (headerMessage) {
        maintenanceMessage = headerMessage;
      }
    }
  }

  // Only show overlay if maintenance mode is explicitly enabled
  if (isMaintenanceModeEnabled) {
    showMaintenanceOverlay(maintenanceMessage);
    
    // Do NOT throw error - this prevents other error handlers from showing their UI
    // Instead, create a promise that never resolves to halt execution
    return new Promise(() => {}); 
  } else {
    // If not maintenance mode, treat as generic 503 error
    throw new Error("The server returned service unavailable (503). Please try again later.");
  }
}

function showMaintenanceOverlay(message: string) {
  // Remove any existing overlays first
  const existingOverlay = document.getElementById("maintenance-overlay");
  if (existingOverlay) {
    return; // Already showing, don't create duplicate
  }

  // Hide all existing modals and dialogs by adding a class
  const allModals = document.querySelectorAll('[role="dialog"], .modal, .dialog, [class*="modal"], [class*="dialog"]');
  allModals.forEach((modal: Element) => {
    if (modal instanceof HTMLElement && modal.id !== 'maintenance-overlay') {
      modal.style.display = 'none';
    }
  });

  // Create overlay using class names instead of inline styles
  const overlay = document.createElement("div");
  overlay.id = "maintenance-overlay";

  const modalBox = document.createElement("div");
  modalBox.className = "maintenance-modal-box";

  const heading = document.createElement("h1");
  heading.className = "maintenance-heading";
  heading.textContent = "Temporarily Unavailable";

  const paragraph = document.createElement("p");
  paragraph.className = "maintenance-message";
  paragraph.textContent = message;

  modalBox.appendChild(heading);
  modalBox.appendChild(paragraph);
  overlay.appendChild(modalBox);

  // Insert at the very end of body to ensure it's on top
  document.body.appendChild(overlay);

  // Add classes to body and html for overflow control
  document.body.classList.add('maintenance-active');
  document.documentElement.classList.add('maintenance-active');

  // Prevent all interactions
  overlay.addEventListener("click", (e) => {
    e.stopPropagation();
    e.preventDefault();
  }, true);

  overlay.addEventListener("keydown", (e) => {
    e.stopPropagation();
    e.preventDefault();
  }, true);

  // Also prevent any background clicks
  const blockBackgroundClicks = (e: Event) => {
    if (!overlay.contains(e.target as Node)) {
      e.stopPropagation();
      e.preventDefault();
    }
  };
  
  document.addEventListener("click", blockBackgroundClicks, true);
  document.addEventListener("keydown", blockBackgroundClicks, true);
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

async function throwHttp500Error(response: Response, defaultErrorMsg: string) {
  let responseText: string = "";
  try {
    responseText = await response.text();
  } catch {
    throw new Error(defaultErrorMsg);
  }

  if (responseText) {
    throw new Error(responseText.split('\n')[0]);
  }

  throw new Error(defaultErrorMsg);
}

async function throwResponseError(response: Response, defaultErrorMsg: string) {
  let jsonResponse: any;

  try {
    jsonResponse = await response.json();
  } catch {
    throw new Error(defaultErrorMsg);
  }
  
  if (isErrorValidationErrorResponse(jsonResponse)) {
    throw jsonResponse;
  } else if (jsonResponse.Message && jsonResponse.AfftectedSws) {
      throw new ResponseErrorforRC(jsonResponse.Message, jsonResponse.ErrorType ?? "Unknown", jsonResponse.AfftectedSws);
  } else if (jsonResponse.Message) {
      throw new ResponseError(jsonResponse.Message, jsonResponse.ErrorType ?? "Unknown");
  } else {
    throw new Error(defaultErrorMsg);
  }
}

